// Libraries
import UserAvatar from "@/components/Avatar/UserAvatar";
import { Link } from "@/i18n/navigation";
import moment from "moment-timezone";
import { capitalize } from "@/helpers/capitalize";
import calculatePlayerLevel from "@/helpers/calculatePlayerLevel";
import { useEffect, useState } from "react";
import { slugify } from "@/helpers/slugify";
import { useLocale, useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

export default function DiscussionCard({ discussion, ...props }) {
    const t = useTranslations();
    const locale = useLocale();

    // State
    const [isSolved, setIsSolved] = useState(false);

    // Calculate author level if XP is available
    const authorLevel = discussion.author?.xp !== undefined
        ? calculatePlayerLevel(discussion.author.xp).level
        : null;

    // Lifecycle
    useEffect(() => {
        if (
            discussion.messages.filter((message) => message.bestMessage)
                .length > 0 ||
            discussion.isForHelp
        ) {
            setIsSolved(true);
        }
    }, []);

    return (
        <Link
            href={`/community/discussions/${discussion.id}-${slugify(
                discussion.title,
            )}`}
            className={`p-5 rounded-2xl border flex-1 transition-all duration-200 relative hover:shadow-lg hover:scale-[1.01] ${
                isSolved
                    ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 hover:border-emerald-400 dark:hover:border-emerald-600"
                    : "bg-white dark:bg-bgDark-840 border-gray-200 dark:border-borderDark hover:border-primary dark:hover:border-primary hover:bg-gray-50 dark:hover:bg-bgDark"
            }`}
        >
            {isSolved && (
                <div
                    className="absolute top-5 right-5 text-emerald-600 dark:text-emerald-400"
                    aria-label="Discussion is solved"
                    title="Discussion résolue"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-8 w-8"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                </div>
            )}
            <div>
                {/* User */}
                <div className="flex items-center gap-4">
                    <div className="relative w-[50px] h-[50px]">
                        <div className="w-[50px] h-[50px] rounded-full overflow-hidden">
                            <UserAvatar
                                user={discussion.author}
                                alt={`Picture ${
                                    discussion.author.pseudo &&
                                    discussion.author.showPseudo
                                        ? discussion.author.pseudo
                                        : `${discussion.author.firstName} ${discussion.author.lastName}`
                                }`}
                                width={50}
                                height={50}
                                className="rounded-full"
                                aria-label="User picture"
                            />
                        </div>
                        {authorLevel && (
                            <div
                                className="absolute -bottom-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-gray-900 dark:bg-white text-white dark:text-grayDark-900 text-[10px] font-semibold flex items-center justify-center"
                                title={t("community_level", { level: authorLevel })}
                            >
                                {authorLevel}
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="text-gray-900 dark:text-white font-medium">
                            {discussion.author.pseudo &&
                            discussion.author.showPseudo
                                ? capitalize(discussion.author.pseudo)
                                : `${capitalize(
                                      discussion.author.firstName,
                                  )} ${capitalize(discussion.author.lastName)}`}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-grayDark-400">
                            {capitalize(
                                moment(discussion.lastActivity)
                                    .locale(locale)
                                    .calendar(null, {
                                        sameDay: t("BookedEvent_today", {
                                            date: "HH:mm",
                                        }),
                                        nextDay: t("BookedEvent_tomorrow", {
                                            date: "HH:mm",
                                        }),
                                        nextWeek: t("BookedEvent_nextWeek", {
                                            day: "dddd",
                                            hour: "HH:mm",
                                        }),
                                        lastDay: t("BookedEvent_yesterday", {
                                            date: "HH:mm",
                                        }),
                                        lastWeek: "dddd",
                                        sameElse: "LLL",
                                    }),
                            )}
                        </div>
                    </div>
                </div>
                <h3 className="mt-4 text-gray-900 dark:text-white font-semibold text-lg">
                    {capitalize(discussion.title)}
                </h3>
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-grayDark-700 text-gray-600 dark:text-grayDark-300">
                        <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                            />
                        </svg>
                        {discussion.messages.length}{" "}
                        {discussion.messages.length > 1
                            ? t("DiscussionCard_replies").toLowerCase()
                            : t("DiscussionCard_reply").toLowerCase()}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-grayDark-700 text-gray-600 dark:text-grayDark-300">
                        <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                        </svg>
                        {discussion.participants.length}{" "}
                        {discussion.participants.length > 1
                            ? t("DiscussionCard_participants").toLowerCase()
                            : t("DiscussionCard_participant").toLowerCase()}
                    </span>
                    {/* Formation/Accelerator tag */}
                    {discussion.session?.part?.course ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary dark:bg-primary/20">
                            <SolarIcon name="book-2-linear" className="w-3.5 h-3.5" />
                            {discussion.session.part.course.name}
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                            <SolarIcon name="rocket-2-linear" className="w-3.5 h-3.5" />
                            {t("community_accelerator")}
                        </span>
                    )}
                </div>
            </div>
        </Link>
    );
}
